import { observer } from 'mobx-react-lite';
import { MouseEvent, ReactNode, useCallback } from 'react';
import { Link, LinkProps, NavigateOptions, NavLink, useNavigate } from 'react-router';
import historyStore from '@client/store/historyStore';
import webViewStore from '@client/store/webViewStore';

interface CommonLinkProps extends Omit<LinkProps, 'className' | 'to' | 'children'> {
    children: ReactNode;
    className?: string | ((props: { isActive: boolean }) => string);
    to: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

/**
 * 플랫폼을 고려한 공통 네비게이션 훅
 */
export const useCommonNavigate = () => {
    const navigate = useNavigate();

    const navigateBack = useCallback(() => {
        if (historyStore.hasHistory()) {
            historyStore.pop();
            navigate(-1);
        } else {
            // 웹뷰일 때 히스토리가 없으면 닫아줌
            if (webViewStore.isWebView && webViewStore.navigationInfo?.context === 'stack') {
                webViewStore.sendMessageToApp('closeWebView');
            } else {
                navigate('/');
            }
        }
    }, [navigate]);

    const commonNavigate = useCallback(
        (to: string, options?: NavigateOptions) => {
            // 웹뷰에서는 모두 새 웹뷰로 연다
            const isOpenInNewWebView = true;

            const isAbsoluteUrl = /^https?:\/\//.test(to);

            // 절대 경로이거나 openInNewWebView 옵션이 true인 경우 웹뷰로 열기
            if (webViewStore.isWebView && (isAbsoluteUrl || isOpenInNewWebView)) {
                // 웹뷰 스택에서 열렸을 때 replace 는 새 웹뷰를 열지 않고 해당 웹뷰에서 그대로 보여준다
                if (
                    options?.replace &&
                    webViewStore.navigationInfo?.context === 'stack' &&
                    !isAbsoluteUrl
                ) {
                    navigate(to, options);
                    return;
                }

                // 상대 경로를 절대 URL로 변환
                const fullUrl = isAbsoluteUrl ? to : `${window.location.origin}${to}`;

                (async () => {
                    try {
                        await webViewStore.sendMessageToApp('openWebView', {
                            url: fullUrl,
                        });
                    } catch (e) {
                        console.error('웹뷰 열기 실패:', e);

                        if (isAbsoluteUrl) {
                            // 절대 경로 실패 시 새 창으로 열기
                            window.open(to, '_blank', 'noopener,noreferrer');
                        } else {
                            // 상대 경로 실패 시 기본 라우팅으로 fallback
                            navigate(to, options);
                        }
                    }
                })();
            } else {
                // 기본 라우팅
                navigate(to, options);
            }
        },
        [navigate],
    );

    return {
        navigate: commonNavigate,
        navigateBack,
    };
};

/**
 * URL 타입에 따라 적절한 컴포넌트로 렌더링하는 공통 링크 컴포넌트
 *
 * ## 동작 흐름
 *
 * ### 1. URL 타입 분석
 * - **절대 경로** (https://..., http://...): `<a>` 태그로 렌더링
 * - **상대 경로** (/home, ../page): React Router Link/NavLink로 렌더링
 *
 * ### 2. 렌더링 컴포넌트 결정 (상대 경로)
 * - **함수형 className**: NavLink 사용 (활성 상태 지원)
 * - **일반 className**: Link 사용
 *
 * ### 3. 클릭 동작 처리
 * - **기존 onClick 실행 후 preventDefault 확인**: 막혀있으면 추가 처리 안함
 * - **절대 경로**:
 *   - 웹뷰 환경: 새 웹뷰로 열기
 *   - 웹 환경: 새 창으로 열기 (target="_blank")
 * - **상대 경로 + openInNewWebView=true**:
 *   - 웹뷰 환경: 새 웹뷰로 열기
 *   - 웹 환경: 기본 React Router 동작
 * - **기본 상대 경로**: React Router 기본 동작
 *
 * ### 4. 웹뷰 처리 실패 시 Fallback
 * - 절대 경로: window.open으로 새 창 열기
 * - 상대 경로: 기본 라우팅으로 처리
 */
export const CommonLink = observer(
    ({ children, className, to, onClick, ...propsToPass }: CommonLinkProps) => {
        const { navigate } = useCommonNavigate();

        // 웹뷰에서는 모두 새 웹뷰로 연다
        const isOpenInNewWebView = true;

        // URL이 절대 경로인지 확인
        const isAbsoluteUrl = /^https?:\/\//.test(to);

        const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
            // 기존 onClick 핸들러 먼저 실행
            if (onClick) {
                onClick(event);
            }

            // 기존 onClick에서 preventDefault를 호출했다면 추가 처리하지 않음
            if (event.defaultPrevented) {
                return;
            }

            // 웹뷰 환경에서만 특별 처리
            if (webViewStore.isWebView && (isAbsoluteUrl || isOpenInNewWebView)) {
                event.preventDefault();
                navigate(to);
            }
            // 웹 환경에서는 기본 동작 (절대 경로: target="_blank", 상대 경로: React Router)
        };

        // 절대 경로는 일반 <a> 태그로 렌더링
        if (isAbsoluteUrl) {
            const linkClassName =
                typeof className === 'function' ? className({ isActive: false }) : className;

            return (
                <a
                    href={to}
                    className={linkClassName}
                    onClick={handleClick}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...propsToPass}
                >
                    {children}
                </a>
            );
        }

        // 상대 경로: 함수형 className이면 NavLink, 아니면 Link
        const isNavLink = typeof className === 'function';

        if (isNavLink) {
            return (
                <NavLink to={to} className={className} onClick={handleClick} {...propsToPass}>
                    {children}
                </NavLink>
            );
        }

        return (
            <Link to={to} className={className} onClick={handleClick} {...propsToPass}>
                {children}
            </Link>
        );
    },
);
